/**
 * Tier configuration for usage limits and feature access.
 *
 * This module defines the subscription tiers and their associated limits.
 * The mapping from Stripe product IDs to tiers is handled server-side
 * in the stripe/get-subscription-status edge function for security.
 */

import type { NotificationChangeType, DigestFrequency } from '@/types/notifications';

/**
 * Available subscription tier names.
 * Tier assignment is determined server-side based on Stripe subscription.
 */
export type TierName = 'free' | 'citizen' | 'professional';

/**
 * Notification-related limits for a tier.
 */
export interface TierNotificationLimits {
  /** Which digest frequencies are available (instant, daily, weekly) */
  allowedFrequencies: DigestFrequency[];
  /** Which change types can use instant delivery */
  instantAllowed: NotificationChangeType[];
  /** Whether new bill notifications are enabled at all */
  newBillsEnabled: boolean;
  /** Whether similar bill notifications are enabled at all */
  similarBillsEnabled: boolean;
}

/**
 * Summary unlock limits for a tier.
 */
export interface TierSummaryLimits {
  /** Number of full summaries that can be unlocked per day (null = unlimited) */
  dailyUnlocks: number | null;
  /** How long each unlock lasts in hours */
  unlockDurationHours: number;
}

/**
 * Complete limit configuration for a subscription tier.
 */
export interface TierLimits {
  /** Display name for the tier */
  displayName: string;
  /** Maximum tracked bills (null = unlimited) */
  trackedBills: number | null;
  /** Maximum tracked committees (null = unlimited) */
  trackedCommittees: number | null;
  /** Maximum saved searches (null = unlimited) */
  savedSearches: number | null;
  /** Notification-related limits */
  notifications: TierNotificationLimits;
  /** Summary access limits */
  summaries: TierSummaryLimits;
}

/**
 * Central tier configuration.
 *
 * Free: Basic access with daily digests only
 * Citizen: Enhanced access with most realtime features
 * Professional: Full access with no limits
 */
export const TIER_CONFIG: Record<TierName, TierLimits> = {
  free: {
    displayName: 'Free',
    trackedBills: 10,
    trackedCommittees: 2,
    savedSearches: 2,
    notifications: {
      allowedFrequencies: ['daily'],
      instantAllowed: [],
      newBillsEnabled: false,
      similarBillsEnabled: false,
    },
    summaries: {
      dailyUnlocks: 5,
      unlockDurationHours: 24,
    },
  },
  citizen: {
    displayName: 'Citizen',
    trackedBills: 50,
    trackedCommittees: 10,
    savedSearches: 10,
    notifications: {
      allowedFrequencies: ['instant', 'daily', 'weekly'],
      instantAllowed: ['status', 'version', 'title'], // New bills are daily only
      newBillsEnabled: true,
      similarBillsEnabled: false,
    },
    summaries: {
      dailyUnlocks: null, // Unlimited
      unlockDurationHours: 24,
    },
  },
  professional: {
    displayName: 'Professional',
    trackedBills: null, // Unlimited
    trackedCommittees: null, // Unlimited
    savedSearches: null, // Unlimited
    notifications: {
      allowedFrequencies: ['instant', 'daily', 'weekly'],
      instantAllowed: ['status', 'version', 'title', 'new_bill', 'similar_bill'],
      newBillsEnabled: true,
      similarBillsEnabled: true,
    },
    summaries: {
      dailyUnlocks: null, // Unlimited
      unlockDurationHours: 24,
    },
  },
};

/**
 * Get tier limits for a given tier name.
 * Defaults to 'free' if tier is not recognized.
 */
export function getTierLimits(tier: TierName | string | null | undefined): TierLimits {
  if (tier && tier in TIER_CONFIG) {
    return TIER_CONFIG[tier as TierName];
  }
  return TIER_CONFIG.free;
}

/**
 * Check if a tier can track more bills given current count.
 */
export function canTrackMoreBills(tier: TierName, currentCount: number): boolean {
  const limits = getTierLimits(tier);
  return limits.trackedBills === null || currentCount < limits.trackedBills;
}

/**
 * Get remaining bill tracking slots for a tier.
 * Returns null if unlimited.
 */
export function getRemainingBillSlots(tier: TierName, currentCount: number): number | null {
  const limits = getTierLimits(tier);
  if (limits.trackedBills === null) {
    return null;
  }
  return Math.max(0, limits.trackedBills - currentCount);
}

/**
 * Check if a tier can track more committees given current count.
 */
export function canTrackMoreCommittees(tier: TierName, currentCount: number): boolean {
  const limits = getTierLimits(tier);
  return limits.trackedCommittees === null || currentCount < limits.trackedCommittees;
}

/**
 * Get remaining committee tracking slots for a tier.
 * Returns null if unlimited.
 */
export function getRemainingCommitteeSlots(tier: TierName, currentCount: number): number | null {
  const limits = getTierLimits(tier);
  if (limits.trackedCommittees === null) {
    return null;
  }
  return Math.max(0, limits.trackedCommittees - currentCount);
}

/**
 * Check if a tier can save more searches given current count.
 */
export function canSaveMoreSearches(tier: TierName, currentCount: number): boolean {
  const limits = getTierLimits(tier);
  return limits.savedSearches === null || currentCount < limits.savedSearches;
}

/**
 * Get remaining saved search slots for a tier.
 * Returns null if unlimited.
 */
export function getRemainingSavedSearchSlots(tier: TierName, currentCount: number): number | null {
  const limits = getTierLimits(tier);
  if (limits.savedSearches === null) {
    return null;
  }
  return Math.max(0, limits.savedSearches - currentCount);
}

/**
 * Check if a tier allows a specific digest frequency.
 */
export function isFrequencyAllowed(tier: TierName, frequency: DigestFrequency): boolean {
  const limits = getTierLimits(tier);
  return limits.notifications.allowedFrequencies.includes(frequency);
}

/**
 * Check if a tier allows instant notifications for a change type.
 */
export function isInstantAllowed(tier: TierName, changeType: NotificationChangeType): boolean {
  const limits = getTierLimits(tier);
  return limits.notifications.instantAllowed.includes(changeType);
}

/**
 * Get the effective frequency for a user based on their tier.
 * If they've selected a frequency not allowed by their tier, returns the best available.
 */
export function getEffectiveFrequency(
  tier: TierName,
  preferredFrequency: DigestFrequency
): DigestFrequency {
  const limits = getTierLimits(tier);
  if (limits.notifications.allowedFrequencies.includes(preferredFrequency)) {
    return preferredFrequency;
  }
  // Default to daily if preferred isn't available
  return 'daily';
}

/**
 * Order of tiers from lowest to highest access level.
 * Used for determining upgrade paths.
 */
export const TIER_ORDER: TierName[] = ['free', 'citizen', 'professional'];

/**
 * Display names for each tier.
 * Use this for consistent tier name display across the UI.
 */
export const TIER_DISPLAY_NAMES: Record<TierName, string> = {
  free: 'Free',
  citizen: 'Citizen',
  professional: 'Professional',
};

/**
 * Check if a tier is a paid tier (not free).
 * Handles null/undefined values safely.
 */
export function isPaidTier(tier: TierName | string | null | undefined): boolean {
  return tier !== 'free' && tier !== null && tier !== undefined;
}

/**
 * Get the display name for a tier.
 * Returns 'Free' for unknown/null tiers.
 */
export function getTierDisplayName(tier: TierName | string | null | undefined): string {
  if (tier && tier in TIER_DISPLAY_NAMES) {
    return TIER_DISPLAY_NAMES[tier as TierName];
  }
  return TIER_DISPLAY_NAMES.free;
}

/**
 * Get the next tier up from the current tier.
 * Returns null if already at the highest tier.
 */
export function getNextTier(currentTier: TierName): TierName | null {
  const currentIndex = TIER_ORDER.indexOf(currentTier);
  if (currentIndex === -1 || currentIndex >= TIER_ORDER.length - 1) {
    return null;
  }
  return TIER_ORDER[currentIndex + 1];
}

/**
 * Check if upgrading from one tier to another is valid.
 */
export function isUpgrade(fromTier: TierName, toTier: TierName): boolean {
  return TIER_ORDER.indexOf(toTier) > TIER_ORDER.indexOf(fromTier);
}
